import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { SESSION_RECORDING_MASK_CLASS } from "_shared/session-recording/session-recording-privacy";
import type { RemoveAllAccessReview } from "permissions/_views/people/person-grants-model";
import { removeAllAccess } from "permissions/_views/wording/permission-wording";
import { useId, useRef, useState } from "react";

/**
 * "Remove all access…", the review step before the grantee-scoped disable (#6176;
 * `docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, "The person page").
 *
 * It lists what goes — the standing grants by name and the per-record grants as counts — says that
 * organization-wide notifications are reduced to the person's own when they have them, offers
 * "Also stop sign-in" when the person holds their own sign-in, and asks for the person's name typed
 * out before the destructive button is enabled. Laid out like Add access: full screen on a phone.
 */

export type RemoveAllAccessDialogProps = Readonly<{
  open: boolean;
  personName: string;
  /**
   * False for a person the directory did not name: the confirmation then asks for a fixed phrase,
   * since there is no name to type, and the action stays available (#6429).
   */
  named?: boolean;
  organization?: string;
  review: RemoveAllAccessReview;
  onClose: () => void;
  /** Resolves true once the server has removed the access, which closes the dialog. */
  onConfirm: (stopSignIn: boolean) => Promise<boolean>;
}>;

/** The typed name matches when it is the name, whatever the case and the spaces around it. */
export function confirmsName(typed: string, personName: string): boolean {
  return typed.trim().toLocaleLowerCase() === personName.trim().toLocaleLowerCase();
}

function ReviewList(props: Readonly<{ heading: string; items: readonly string[]; empty: string }>) {
  const headingId = useId();
  return (
    <Box>
      <Typography id={headingId} variant="overline" color="text.secondary" component="h3">
        {props.heading}
      </Typography>
      {props.items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {props.empty}
        </Typography>
      ) : (
        <Box component="ul" aria-labelledby={headingId} sx={{ m: 0, pl: 2.5, typography: "body2" }}>
          {props.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default function RemoveAllAccessDialog(props: RemoveAllAccessDialogProps) {
  const { open, personName, organization, review, onClose, onConfirm } = props;
  const named = props.named ?? true;
  const confirmText = named ? personName : removeAllAccess.unnamedConfirmText;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const titleId = useId();
  const bodyId = useId();
  const signInHelpId = useId();
  const [typed, setTyped] = useState("");
  const [stopSignIn, setStopSignIn] = useState(false);
  const [saving, setSaving] = useState(false);
  const confirmRef = useRef<HTMLInputElement>(null);

  const close = () => {
    setTyped("");
    setStopSignIn(false);
    setSaving(false);
    onClose();
  };

  const submit = async () => {
    setSaving(true);
    const done = await onConfirm(stopSignIn);
    setSaving(false);
    if (done) close();
  };

  const confirmed = confirmsName(typed, confirmText);

  return (
    <Dialog
      open={open}
      onClose={close}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      slotProps={{ transition: { onEntered: () => confirmRef.current?.focus() } }}
    >
      <DialogTitle id={titleId}>{removeAllAccess.title(personName)}</DialogTitle>
      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <DialogContentText id={bodyId}>{removeAllAccess.intro(personName, organization)}</DialogContentText>
        <ReviewList heading={removeAllAccess.standingHeading} items={review.standing} empty={removeAllAccess.noStandingGrants} />
        <ReviewList heading={removeAllAccess.recordsHeading} items={review.records} empty={removeAllAccess.noRecordGrants} />
        {review.lowersNotifications && (
          <Typography variant="body2" color="text.secondary">
            {removeAllAccess.notificationsReduced(personName)}
          </Typography>
        )}
        {review.holdsOwnSignIn && (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={stopSignIn}
                  onChange={(event) => setStopSignIn(event.target.checked)}
                  slotProps={{ input: { "aria-describedby": signInHelpId } }}
                />
              }
              label={removeAllAccess.stopSignIn}
            />
            <FormHelperText id={signInHelpId} sx={{ mt: 0 }}>
              {removeAllAccess.stopSignInHelp}
            </FormHelperText>
          </Box>
        )}
        <TextField
          inputRef={confirmRef}
          fullWidth
          autoComplete="off"
          label={named ? removeAllAccess.confirmLabel(personName) : removeAllAccess.unnamedConfirmLabel}
          // The label names the person (#6330).
          slotProps={{ inputLabel: { className: SESSION_RECORDING_MASK_CLASS } }}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && confirmed && !saving) void submit();
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button variant="contained" color="error" disabled={!confirmed || saving} onClick={() => void submit()}>
          {removeAllAccess.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
